import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { baseConfig } from "./../../../axiosConfig";
import { Context } from "./../../../contexts/Store";
import Loader from "../../includes/loader/Loader";
import moment from "moment";

// Icon imports
import submitIcon from "../../../assets/icons/status-paid-icon.svg";
import pendingIcon from "../../../assets/icons/status-pending-icon.svg";
import lockIcon from "../../../assets/icons/close.png";
import returnIcon from "../../../assets/icons/status-due-icon.svg";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";
import pptIcon from "../../../assets/icons/ppt-icon.svg";
import docIcon from "../../../assets/icons/doc-icon.svg";
import xlsxIcon from "../../../assets/icons/xlsx-icon.svg";
import pngIcon from "../../../assets/icons/png-icon.svg";
import jpgIcon from "../../../assets/icons/jpg-icon.svg";
import jpegIcon from "../../../assets/icons/jpeg-icon.svg";
import fileIcon from "../../../assets/icons/doc-note-icon.svg";
import mp4Icon from "../../../assets/icons/mp4-icon.svg";

import NormalSubmissionSubmittedFile from "./NormalSubmissionSubmittedFile";
import AssignmentQnCard from "../../includes/assignmentCommonComponents/AssignmentQnCard";
import SubmitNormal from "./SubmitNormal";
import SubmitChapterWise from "./SubmitChapterWise";
import SubmitDissertation from "./SubmitDissertation";

const getStatusIcon = (status) => {
  switch (status) {
    case "Active":
      return pendingIcon;
    case "Approved":
    case "Submitted":
      return submitIcon;
    case "Closed":
      return lockIcon;
    case "Returned":
      return returnIcon;
    default:
      return "";
  }
};
// ============= REDUCER FOR QUESTION FILES =============
const ACTIONS = {
  INITIALIZE_FILES: "INITIALIZE_FILES",
  UPLOAD_FILE: "UPLOAD_FILE",
  REMOVE_FILE: "REMOVE_FILE",
  CLEAR_ALL_FILES: "CLEAR_ALL_FILES",
  UPDATE_FILE_STATUS: "UPDATE_FILE_STATUS",
};

const createInitialState = (questions) => {
  const files = {};
  questions?.forEach((question) => {
    files[question.id] = {
      file: null,
      fileName: "",
      fileSize: 0,
      fileType: "",
      uploadedAt: null,
      isSubmitted: false,
      questionNo: question.question_no,
      error: null,
    };
  });
  return files;
};

const submittedFilesReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.INITIALIZE_FILES:
      return createInitialState(action.payload.questions);

    case ACTIONS.UPLOAD_FILE:
      const { questionId, file } = action.payload;
      return {
        ...state,
        [questionId]: {
          ...state[questionId],
          file: file,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadedAt: new Date().toISOString(),
          isSubmitted: true,
          error: null,
        },
      };

    case ACTIONS.REMOVE_FILE:
      return {
        ...state,
        [action.payload.questionId]: {
          ...state[action.payload.questionId],
          file: null,
          fileName: "",
          fileSize: 0,
          fileType: "",
          uploadedAt: null,
          isSubmitted: false,
          error: null,
        },
      };

    case ACTIONS.UPDATE_FILE_STATUS:
      const { questionId: qId, updates } = action.payload;
      return {
        ...state,
        [qId]: {
          ...state[qId],
          ...updates,
        },
      };

    case ACTIONS.CLEAR_ALL_FILES:
      const clearedState = {};
      Object.keys(state).forEach((questionId) => {
        clearedState[questionId] = {
          ...state[questionId],
          file: null,
          fileName: "",
          fileSize: 0,
          fileType: "",
          uploadedAt: null,
          isSubmitted: false,
          error: null,
        };
      });
      return clearedState;

    default:
      return state;
  }
};

const renderFilePreview = (file) => {
  if (!file) return null;
  return (
    <UploadedFilePreview target="_blank" rel="noopener noreferrer" href={file}>
      <FileIcon src={pdfIcon} />
      <FileDetails>
        <UploadedFileName>Question file</UploadedFileName>
      </FileDetails>
    </UploadedFilePreview>
  );
};

// ============= MAIN COMPONENT =============
function SubmitAssignment({ data, setSinglePage, refreshApi }) {
  const { state } = useContext(Context);
  const navigate = useNavigate();
  const { id } = useParams();

  // State management - kept flexible for future additions
  const [isLoading, setLoading] = useState(data ? false : true);

  const [fileArray, setFileArray] = useState(() => {
    if (!data) return [];

    if (data.data.is_chapterwise === true) {
      // Chapter-wise assignments
      const chapters = [];
      for (let i = 1; i <= data.data.chapter_count; i++) {
        chapters.push({
          id: i,
          fileRequirement: data.data.file_requirements || [],
          sectionName: `chapter ${i}`,
        });
      }
      chapters.push({
        id: data.data.chapter_count + 1,
        sectionName: `Draft Submission`,
        fileRequirement: data.data.file_requirements || [],
      });
      chapters.push({
        id: data.data.chapter_count + 2,
        sectionName: `Final Submission`,
        fileRequirement: [
          ...(data.data.file_requirements || []),
          { file_type: "Similarity Report", count: 1 },
        ],
      });
      return chapters;
    } else if (data.data.assignment_type === "qstn_wise") {
      // For question-wise assignments, we don't need fileArray
      return [];
    } else {
      // Regular assignments
      return data.data.file_requirements || [];
    }
  });

  const [selectedFile, setSelectedFile] = useState(() => {
    if (!data) return null;

    if (data.data.is_chapterwise === true) {
      // For chapter-wise, return first chapter object
      return {
        id: 1,
        fileRequirement: data.data.file_requirements || [],
        sectionName: `chapter 1`,
      };
    } else if (data.data.assignment_type === "qstn_wise") {
      // For question-wise assignments, we don't need selectedFile
      return null;
    } else {
      // For regular assignments, return first file type string
      return data.data.file_requirements?.[0]?.file_type || null;
    }
  });

  const [assignment, setAssignment] = useState(data ? data.data : null);
  const [assignmentDetails, setAssignmentDetails] = useState(
    data ? data : null
  );
  const [selectedViewmore, setSelectedViewmore] = useState("");

  // Initialize useReducer for question files
  const [questionFiles, dispatch] = useReducer(
    submittedFilesReducer,
    assignment?.questions || [],
    createInitialState
  );

  // Action creators for file management
  const fileActions = useMemo(
    () => ({
      initializeFiles: (questions) => {
        dispatch({
          type: ACTIONS.INITIALIZE_FILES,
          payload: { questions },
        });
      },

      uploadFile: (questionId, file) => {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          dispatch({
            type: ACTIONS.UPDATE_FILE_STATUS,
            payload: {
              questionId,
              updates: { error: "File size must be less than 10MB" },
            },
          });
          return false;
        }

        dispatch({
          type: ACTIONS.UPLOAD_FILE,
          payload: { questionId, file },
        });
        return true;
      },

      removeFile: (questionId) => {
        dispatch({
          type: ACTIONS.REMOVE_FILE,
          payload: { questionId },
        });
      },

      clearAllFiles: () => {
        dispatch({ type: ACTIONS.CLEAR_ALL_FILES });
      },
    }),
    []
  );

  // Selectors for file data
  const fileSelectors = useMemo(
    () => ({
      hasSubmittedFile: (questionId) => {
        return questionFiles[questionId]?.isSubmitted || false;
      },

      getFileData: (questionId) => {
        return questionFiles[questionId] || null;
      },

      getAllSubmittedFiles: () => {
        let fileIndex = 0;

        const filtered = Object.entries(questionFiles).filter(
          ([id, fileData]) => {
            const question = assignment?.questions?.find((q) => q.id === id);
            const hasLocalFile = !!fileData.file;
            const hasApprovedFile =
              question?.qstn_wise_submission_status === "approved" &&
              !!question?.latest_file;

            // Include if has local file OR has approved file from backend
            return hasLocalFile || hasApprovedFile;
          }
        );

        return filtered.map(([id, fileData]) => {
          const question = assignment?.questions?.find((q) => q.id === id);
          const isApproved =
            question?.qstn_wise_submission_status === "approved";

          if (isApproved && question?.latest_file) {
            // Approved: Use backend file (latest_file as URL string)
            const currentIndex = fileIndex++;
            return {
              questionId: id,
              questionNo: question.question_no,
              file: question.latest_file, // URL string from backend
              fileName: question.latest_file_name,
              fileSize: 0, // Backend doesn't provide size
              fileType: question.latest_file_type,
              uploadedAt: question.latest_file_uploaded_date,
              fileIndex: currentIndex,
              isApproved: true,
            };
          } else {
            // Not approved: Use local file
            const currentIndex = fileIndex++;
            return {
              questionId: id,
              questionNo: question?.question_no,
              file: fileData.file, // File object
              fileName: fileData.file.name,
              fileSize: fileData.file.size,
              fileType: fileData.file.type,
              uploadedAt: new Date().toISOString(),
              fileIndex: currentIndex,
              isApproved: false,
            };
          }
        });
      },

      getSubmissionStats: () => {
        const total = Object.keys(questionFiles).length;
        const submitted = Object.values(questionFiles).filter(
          (f) => f.isSubmitted
        ).length;
        return {
          total,
          submitted,
          pending: total - submitted,
          completionPercentage:
            total > 0 ? Math.round((submitted / total) * 100) : 0,
        };
      },
    }),
    [questionFiles]
  );

  // ============= API CALLS =============
  const getAssignment = useCallback(() => {
    setLoading(true);
    let accessToken = state.user_details.access_token;

    baseConfig
      .get(`/students/student-assignment/${id}/`, {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      })
      .then((response) => {
        const { StatusCode, data } = response.data;
        if (StatusCode === 6000) {
          setAssignment(data.data);
          setAssignmentDetails(data);
          setLoading(false);

          // Handle different assignment types
          if (data.data.assignment_type === "qstn_wise") {
            // Initialize files for question-wise assignments
            fileActions.initializeFiles(data.data.questions);
          } else if (data.data.is_chapterwise === true) {
            // Chapter-wise assignments
            const chapters = [];
            for (let i = 1; i <= data.data.chapter_count; i++) {
              chapters.push({
                id: i,
                fileRequirement: data.data.file_requirements,
                sectionName: `chapter ${i}`,
              });
            }
            chapters.push({
              id: data.data.chapter_count + 1,
              sectionName: `Draft Submission`,
              fileRequirement: data.data.file_requirements,
            });
            chapters.push({
              id: data.data.chapter_count + 2,
              sectionName: `Final Submission`,
              fileRequirement: [
                ...data.data.file_requirements,
                { file_type: "Similarity Report", count: 1 },
              ],
            });

            setFileArray(chapters);
            setSelectedFile(chapters[0]);
          } else {
            // Regular assignments
            setSelectedFile(data.data.file_requirements[0]?.file_type);
            setFileArray([...data.data.file_requirements]);
          }
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        if (error?.response?.data?.code === "token_not_valid") {
          localStorage.clear();
          window.location = "/";
        }
        console.error("Error fetching assignment:", error);
        setLoading(false);
      });
  }, [id, state.user_details.access_token, data, fileActions]);

  const submitAssignmentFile = useCallback(() => {
    let accessToken = state.user_details.access_token;
    const formdata = new FormData();
    // Add files for question-wise assignments
    if (assignment?.assignment_type === "qstn_wise") {
      const submittedFiles = fileSelectors.getAllSubmittedFiles();

      // Validate that all questions have files if required
      if (submittedFiles.length === 0) {
        alert("Please upload at least one file before submitting.");
        return;
      }
      const submission_data = {
        submission_type: "question_wise",
        files: [],
      };

      submittedFiles.forEach((fileData, index) => {
        // Add actual file with indexed key
        formdata.append(`files[${index}]`, fileData.file);

        // Build metadata structure
        submission_data.files.push({
          id: fileData.questionId,
          question_no: fileData.questionNo,
          file_name: fileData.fileName,
          file_size: fileData.fileSize,
          file_type: fileData.fileType,
          file_index: index,
          uploaded_at: fileData.uploadedAt,
        });
      });

      // Add metadata as JSON
      formdata.append("submission_data", JSON.stringify(submission_data));
    }
    setLoading(true);

    baseConfig
      .post(
        `/students/assignment-submit/${id || assignmentDetails.assignment_id}/`,
        formdata,
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        }
      )
      .then((response) => {
        const { StatusCode } = response.data;
        if (StatusCode === 6000) {
          setLoading(false);
          setSinglePage && setSinglePage(false);
          refreshApi && refreshApi();
          if (!setSinglePage && !refreshApi) {
            navigate("/assignments/");
          }
        } else {
          alert("Contact LMS");
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Submit error:", error);
        alert("Contact LMS");
      });
  }, [
    assignment,
    fileSelectors,
    id,
    assignmentDetails,
    state.user_details.access_token,
    setSinglePage,
    refreshApi,
    navigate,
  ]);

  // ============= EFFECTS =============
  useEffect(() => {
    if (!data && id) {
      getAssignment();
    }
  }, [data, id, getAssignment]);

  // Initialize files when assignment data is available
  useEffect(() => {
    if (assignment?.assignment_type === "qstn_wise" && assignment.questions) {
      fileActions.initializeFiles(assignment.questions);
    }
  }, [assignment, fileActions]);

  // ============= RENDER =============
  if (isLoading) {
    return (
      <MainContainer>
        <Loader />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      {!data && (
        <>
          <TextDiv>
            <PathText onClick={() => navigate("/assignments/")}>
              Assignments
            </PathText>
            <Slash>&nbsp;/&nbsp;</Slash>
            <PathTextBlack>{assignmentDetails?.subject_name}</PathTextBlack>
          </TextDiv>
          <AssignmentTitleText>{assignment?.title}</AssignmentTitleText>
          <MetaInfoContainer>
            <TotalQuestions>
              Total Questions: {assignment?.total_questions}
            </TotalQuestions>
            {assignment?.question_type === "grade" && (
              <TotalQuestions>
                Total Marks: {assignment?.total_mark}
              </TotalQuestions>
            )}
            <TotalQuestions>
              Deadline: {moment(assignment?.deadline).format("DD-MM-YYYY")}
            </TotalQuestions>
            <StatusButton>
              <StatusIcon
                src={getStatusIcon(assignmentDetails?.status)}
                alt="icon"
              />
              <StatusText>{assignmentDetails?.status}</StatusText>
            </StatusButton>
            {assignment?.question_file && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                {renderFilePreview(assignment?.question_file)}
              </div>
            )}
          </MetaInfoContainer>
        </>
      )}

      <ScrollContainer>
        {/* Always show instructions and questions for all assignment types */}
        <InstructionsText>Instructions</InstructionsText>
        <InstructionDiv>
          {assignment?.instructions.map((instruction, ind) => (
            <InstructionList key={ind}>{instruction}</InstructionList>
          ))}
        </InstructionDiv>

        <QuestionsText>
          {assignment?.questions?.length > 1 ? "Questions" : "Question"}
        </QuestionsText>
        {assignment?.questions?.length > 0 && (
          <QuestionsWrapper>

            {assignment.questions.map((question, index) => (
              <AssignmentQnCard
                data={question}
                key={question.id}
                index={index}
                selectedViewmore={selectedViewmore}
                setSelectedViewmore={setSelectedViewmore}
                // Props for question-wise file handling
                isQuestionWise={assignment?.assignment_type === "qstn_wise"}
                hasFile={fileSelectors.hasSubmittedFile(question.id)}
                fileData={fileSelectors.getFileData(question.id)}
                onFileUpload={(file) =>
                  fileActions.uploadFile(question.id, file)
                }
                onFileRemove={() => fileActions.removeFile(question.id)}
              />
            ))}
          </QuestionsWrapper>
        )}

        {assignmentDetails &&
          assignment.assignment_type === "entire_qstn" &&
          !assignmentDetails.data.is_chapterwise &&
          assignmentDetails.data.is_dissertation && (
            <SubmitDissertation
              setSinglePage={setSinglePage}
              refreshApi={refreshApi}
              assignmentDetails={assignmentDetails}
              fileArray={fileArray}
              setFileArray={setFileArray}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          )}

        {assignmentDetails &&
          assignment.assignment_type === "entire_qstn" &&
          !assignmentDetails.data.is_chapterwise &&
          !assignmentDetails.data.is_dissertation && (
            <SubmitNormal
              setSinglePage={setSinglePage}
              refreshApi={refreshApi}
              assignmentDetails={assignmentDetails}
              fileArray={fileArray}
              setFileArray={setFileArray}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          )}

        {assignmentDetails &&
          assignment.assignment_type === "entire_qstn" &&
          assignmentDetails.data.is_chapterwise && (
            <SubmitChapterWise
              setSinglePage={setSinglePage}
              refreshApi={refreshApi}
              assignmentDetails={assignmentDetails}
              fileArray={fileArray}
              setFileArray={setFileArray}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          )}

        {assignmentDetails && assignment.assignment_type === "qstn_wise" && (
          <>
            <div
              style={{
                display: "flex",
                marginTop: "10px",
                marginRight: "10px",
                justifyContent: "flex-end",
              }}
            >
              <SubmitButton
                onClick={submitAssignmentFile}
                disabled={
                  (assignment.assignment_type === "qstn_wise" &&
                  !(
                    fileSelectors.getSubmissionStats().submitted ===
                    assignment?.questions.filter(
                      (q) => q.qstn_wise_submission_status !== "approved"
                    ).length
                  )) || assignmentDetails.status === "Submitted"
                }
              >
                Submit
              </SubmitButton>
            </div>
            {assignmentDetails?.submitted_files?.length > 0 && (
                               <NormalSubmissionSubmittedFile submittedFiles = {assignmentDetails?.submitted_files}/>
                            )}
          </>
          
        )}  
      </ScrollContainer>
    </MainContainer>
  );
}

export default SubmitAssignment;

// ============= STYLED COMPONENTS =============
const MainContainer = styled.div`
  background: #ffffff;
  padding: 10px 20px 20px 20px;
  border: 1px solid #e5e5e5;
  box-sizing: border-box;
  border-radius: 10px;
  min-height: calc(50vh - 110px);
  overflow-y: scroll;
`;

const TextDiv = styled.div``;

const UploadedFilePreview = styled.a`
  display: flex;
  text-decoration: none;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 12px;
  margin-bottom: 10px;
  min-width: 150px;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #98cbfeff;
  }
`;

const FileIcon = styled.img`
  width: 15px;
  height: 15px;
  object-fit: contain;
  margin-right: 8px;
`;

const FileDetails = styled.div`
  flex: 1;
  margin-left: 8px;
`;

const UploadedFileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const PathText = styled.span`
  display: inline-block;
  font-size: 14px;
  color: #9d9d9d;
  cursor: pointer;
  margin-bottom: 30px;
  &:hover {
    text-decoration: underline;
  }
`;

const Slash = styled.span`
  display: inline-block;
  font-size: 14px;
  color: #9d9d9d;
`;

const PathTextBlack = styled.span`
  color: #000;
  display: inline-block;
  font-size: 14px;
`;

const AssignmentTitleText = styled.h6`
  font-size: 17px;
  font-weight: 500;
  color: #010101;
  line-height: 1.5rem;
`;

const MetaInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
`;

const TotalQuestions = styled.span`
  display: inline-block;
  font-size: 14px;
  color: #9d9d9d;
  margin-top: 5px;
  &:last-child {
    margin-right: 0;
  }
`;

const ScrollContainer = styled.div``;

const InstructionsText = styled.p`
  text-transform: uppercase;
  margin-top: 10px;
  font-size: 17px;
  font-weight: 500;
  color: #010101;
`;

const InstructionDiv = styled.ul`
  margin-top: 5px;
  padding: 0 20px 0px 0px;
  list-style: none;
`;

const InstructionList = styled.li`
  font-size: 14px;
  color: #9d9d9d;
`;

const QuestionsWrapper = styled.div`
  padding: 15px;
  background: #f7f7f7;
  border-radius: 10px;
`;

const QuestionsText = styled.h6`
  color: #000;
  font-size: 1rem;
  margin-top: 10px;
  padding: 10px 0;
  text-transform: uppercase;
`;

const StatusButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  box-sizing: border-box;
  border: 1px solid #c1c1c1;
  border-radius: 63px;
  padding: 0 8px;
`;

const StatusIcon = styled.img`
  width: 12px;
  display: block;
  object-fit: cover;
  margin-right: 6px;
`;

const StatusText = styled.p`
  color: #404040;
  font-size: 13px;
  margin-bottom: -2px;
`;

const SubmitButton = styled.button`
  display: inline-block;
  height: 40px;
  padding: 12px 20px;
  background: #917afd;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.05rem;
  color: #fff;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #8069e8;
    box-shadow: 0 4px 8px rgba(145, 122, 253, 0.3);
    // transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #828282ff;
  }
`;
